import { z } from 'zod';

import {
    DEFAULT_OUTREACH_LIVE_WINDOW,
    MAX_OUTREACH_CUSTOM_RANGE_DAYS,
    MAX_OUTREACH_CUSTOM_RANGE_MS,
    type OutreachLiveWindow,
    type OutreachUrgencyFilter,
} from '@apps/web/components/admin/outreach_observability/constants';
import { OUTREACH_TAB_VALUES } from '@apps/web/components/admin/outreach_observability/outreach_tabs';
import {
    type ApiQueryParamsType,
    type GenerationComparisonSection,
} from '@libraries/typescript/api_client';

const OUTREACH_URGENCY_VALUES = [
    'all',
    'normal',
    'urgent',
] as const satisfies ReadonlyArray<OutreachUrgencyFilter>;

const OUTREACH_LIVE_WINDOW_VALUES = [
    'trailing_24h',
    'today',
    'trailing_3d',
    'trailing_7d',
    'trailing_14d',
] as const satisfies ReadonlyArray<OutreachLiveWindow>;

export const OUTREACH_DAYS_VALUES = [7, 14, 28] as const;
export type OutreachAgentDays = (typeof OUTREACH_DAYS_VALUES)[number];
export const OUTREACH_AGENT_DEFAULT_DAYS: OutreachAgentDays = 14;
export const OUTREACH_PORT_DEFAULT_DAYS: OutreachAgentDays = 28;

const OUTREACH_AGENT_GENERATION_VALUES = [
    'v2',
    'v3',
] as const satisfies ReadonlyArray<
    NonNullable<
        ApiQueryParamsType<
            '/admin/outreach-observability',
            'get'
        >['agent_generation']
    >
>;
export type OutreachAgentGeneration =
    (typeof OUTREACH_AGENT_GENERATION_VALUES)[number];
const OUTREACH_AGENT_DEFAULT_GENERATION: OutreachAgentGeneration = 'v3';

export const OUTREACH_WEEKLY_WEEKS_VALUES = [4, 8, 12] as const;
export type OutreachWeeklyWeeks = (typeof OUTREACH_WEEKLY_WEEKS_VALUES)[number];
// The default matches the backend's AUTO_OUTREACH_WEEKLY_WEEKS; the API caps
// the span at 12 weeks.
const DEFAULT_WEEKLY_WEEKS: OutreachWeeklyWeeks = 4;

const OUTREACH_PORT_COHORT_VALUES = [
    'matched',
    'all',
] as const satisfies ReadonlyArray<GenerationComparisonSection['cohort']>;
export type OutreachPortCohort = (typeof OUTREACH_PORT_COHORT_VALUES)[number];

const OUTREACH_PORT_LEAD_TIME_VALUES = [
    'all',
    'under_4h',
    '4h_to_24h',
    'over_24h',
] as const satisfies ReadonlyArray<
    GenerationComparisonSection['strata'][number]['lead_time']
>;
export type OutreachPortLeadTime =
    (typeof OUTREACH_PORT_LEAD_TIME_VALUES)[number];

/** Why a complete custom range cannot be applied, or null when it is valid. */
export function outreachCustomRangeError(
    start: string,
    end: string,
): string | null {
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (startMs >= endMs) {
        return 'Start must be before end.';
    }
    if (endMs - startMs > MAX_OUTREACH_CUSTOM_RANGE_MS) {
        return `Range is limited to ${MAX_OUTREACH_CUSTOM_RANGE_DAYS} days.`;
    }
    return null;
}

const instantSchema = z.iso.datetime({ offset: true });

const organizationIdSchema = z.guid();

// A hand-written URL can hold one id as a string, or a malformed entry.
const organizationIdsSchema = z
    .union([z.string(), z.array(z.unknown())])
    .transform((value) => {
        const ids = [value]
            .flat()
            .flatMap(
                (entry) => organizationIdSchema.safeParse(entry).data ?? [],
            );
        return ids.length > 0 ? ids : undefined;
    })
    .optional()
    .catch(undefined);

function literalSchema<T extends number>(values: ReadonlyArray<T>) {
    return z.coerce
        .number()
        .pipe(z.union(values.map((value) => z.literal(value))));
}

export const outreachSearchSchema = z
    .object({
        tab: z.enum(OUTREACH_TAB_VALUES).catch('health'),
        // Absent means every organization.
        org: organizationIdsSchema,
        urgency: z.enum(OUTREACH_URGENCY_VALUES).catch('all'),
        window: z
            .enum(OUTREACH_LIVE_WINDOW_VALUES)
            .catch(DEFAULT_OUTREACH_LIVE_WINDOW),
        from: instantSchema.optional().catch(undefined),
        to: instantSchema.optional().catch(undefined),
        // Absent means the open tab's default (Agent 14, Port 28).
        days: literalSchema(OUTREACH_DAYS_VALUES).optional().catch(undefined),
        generation: z
            .enum(OUTREACH_AGENT_GENERATION_VALUES)
            .catch(OUTREACH_AGENT_DEFAULT_GENERATION),
        weeks: literalSchema(OUTREACH_WEEKLY_WEEKS_VALUES).catch(
            DEFAULT_WEEKLY_WEEKS,
        ),
        cohort: z.enum(OUTREACH_PORT_COHORT_VALUES).catch('matched'),
        lead_time: z.enum(OUTREACH_PORT_LEAD_TIME_VALUES).catch('all'),
    })
    .transform((search) =>
        search.from !== undefined &&
        search.to !== undefined &&
        outreachCustomRangeError(search.from, search.to) === null
            ? search
            : { ...search, from: undefined, to: undefined },
    );

export type OutreachSearch = z.infer<typeof outreachSearchSchema>;
